import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";
import { useShallow } from "zustand/react/shallow";
import { History } from "lucide-react";
import { AssetSchema } from "@forgepoint/shared";
import type { HydratedItem } from "@forgepoint/shared";
import { Skeleton } from "@/components/ui/skeleton";
import { fetchApi } from "@/lib/api";
import { formatLongDateOnly } from "@/lib/formatDate";
import { useActivityEventsStore } from "@/store/activityEventsStore";
import { useOperationsStore } from "@/modules/operations/operationsStore";
import { usePriceModelsStore } from "@/modules/priceModels/priceModelsStore";
import { useFilesStore } from "@/modules/files/filesStore";
import { useItemDraftsStore } from "./itemDraftsStore";
import { useItemsStore } from "./itemsStore";
import { ItemHistoryEntry } from "./ItemHistoryEntry";
import { ItemTabError } from "./ItemTabError";
import { ItemHistoryUnsavedGroup } from "./ItemHistoryUnsavedGroup";
import {
  draftChanges,
  groupEventsByDay,
  isItemEvent,
  referencedEntityIds,
  ITEM_HISTORY_RECORDED_SINCE,
} from "./itemHistoryLabels";
import type { ItemFieldTarget, ItemHistoryContext } from "./itemHistoryLabels";

const GetAssetsByIdsResponseSchema = z.object({
  assets: z.array(AssetSchema),
});

interface ItemHistoryTabProps {
  item: HydratedItem;
  onSelectField: (target: ItemFieldTarget) => void;
}

/**
 * The item's change log: one group per write, one row per changed field or
 * list, with the user's unsaved edits pinned on top.
 * @param item - The item the editor displays, whose history to show
 * @param onSelectField - Jumps the form to a clicked field
 */
export const ItemHistoryTab = ({
  item,
  onSelectField,
}: ItemHistoryTabProps) => {
  const itemId = item.id;
  const { fetchActivityEvents, getLoadingState } = useActivityEventsStore();
  // Shallow-compared, so the list keeps its identity until an event changes.
  const entityEvents = useActivityEventsStore(
    useShallow((state) => state.getActivityEventsForEntity("ITEM", itemId)),
  );
  const items = useItemsStore((state) => state.items);
  const fetchItem = useItemsStore((state) => state.fetchItem);
  const operations = useOperationsStore((state) => state.operations);
  const fetchOperation = useOperationsStore((state) => state.fetchOperation);
  const priceModels = usePriceModelsStore((state) => state.priceModels);
  const vaultFiles = useFilesStore((state) => state.files);
  const draft = useItemDraftsStore((state) => state.draftsByItemId.get(itemId));
  const events = useMemo(
    () => entityEvents.filter(isItemEvent),
    [entityEvents],
  );
  const { isLoading, error } = getLoadingState("ITEM", itemId);

  const operationNames = useMemo(() => {
    const names = new Map<string, string>();
    for (const [operationId, operation] of operations) {
      names.set(operationId, operation.name);
    }
    for (const row of item.operations ?? []) {
      names.set(row.operationId, row.operation.name);
    }
    return names;
  }, [operations, item]);

  const itemNames = useMemo(() => {
    const names = new Map<string, string>();
    for (const [id, cachedItem] of items) {
      names.set(id, cachedItem.name);
    }
    for (const row of item.components ?? []) {
      names.set(row.itemId, row.component.name);
    }
    for (const row of item.upgrades ?? []) {
      names.set(row.itemId, row.upgrade.name);
    }
    return names;
  }, [items, item]);

  const priceModelNames = useMemo(
    () =>
      new Map([...priceModels.values()].map((model) => [model.id, model.name])),
    [priceModels],
  );

  const knownFileNames = useMemo(() => {
    const names = new Map<string, string>();
    for (const [id, file] of vaultFiles) {
      names.set(id, file.name);
    }
    for (const file of item.vaultFiles ?? []) {
      names.set(file.id, file.name);
    }
    for (const asset of [item.primaryFileAsset, ...(item.fileAssets ?? [])]) {
      if (asset) {
        names.set(asset.id, asset.fileName);
      }
    }
    return names;
  }, [vaultFiles, item]);

  const referenced = useMemo(
    () =>
      referencedEntityIds([
        ...events.flatMap((event) => event.eventMetadata.changes),
        ...draftChanges(draft),
      ]),
    [events, draft],
  );
  const [assetNames, setAssetNames] = useState<ReadonlyMap<string, string>>(
    new Map(),
  );
  const fileNames = new Map([...knownFileNames, ...assetNames]);

  // Rows the item no longer holds carry only ids, so their names are fetched
  // once each; the ref keeps a failed fetch from retrying on every render.
  // Unlinking a drawing keeps its asset, so a removed drawing still resolves.
  const requestedIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const { itemIds, operationIds, assetIds } = referenced;
    for (const id of itemIds) {
      if (itemNames.has(id) || requestedIdsRef.current.has(id)) {
        continue;
      }
      requestedIdsRef.current.add(id);
      fetchItem(id).catch(() => undefined);
    }
    for (const id of operationIds) {
      if (operationNames.has(id) || requestedIdsRef.current.has(id)) {
        continue;
      }
      requestedIdsRef.current.add(id);
      fetchOperation(id).catch(() => undefined);
    }
    const missingAssetIds = assetIds.filter(
      (id) => !knownFileNames.has(id) && !requestedIdsRef.current.has(id),
    );
    if (missingAssetIds.length === 0) {
      return;
    }
    missingAssetIds.forEach((id) => requestedIdsRef.current.add(id));
    fetchApi(`api/assets?ids=${missingAssetIds.join(",")}`, "GET")
      .then((response) => {
        const parsed = response.ok
          ? GetAssetsByIdsResponseSchema.safeParse(response.data)
          : null;
        if (!parsed?.success) {
          return;
        }
        setAssetNames(
          (previous) =>
            new Map([
              ...previous,
              ...parsed.data.assets.map(
                (asset) => [asset.id, asset.fileName] as const,
              ),
            ]),
        );
      })
      .catch(() => undefined);
  }, [
    referenced,
    itemNames,
    operationNames,
    knownFileNames,
    fetchItem,
    fetchOperation,
  ]);

  if (isLoading && events.length === 0) {
    return <HistorySkeleton />;
  }

  if (error && events.length === 0) {
    return (
      <ItemTabError
        message={error}
        onRetry={() =>
          fetchActivityEvents("ITEM", itemId).catch(() => undefined)
        }
      />
    );
  }

  const context: ItemHistoryContext = {
    item,
    operationNames,
    itemNames,
    fileNames,
    priceModelNames,
  };
  const recordedSince = formatLongDateOnly(ITEM_HISTORY_RECORDED_SINCE);
  const unsavedGroup = (
    <ItemHistoryUnsavedGroup
      draft={draft}
      context={context}
      onSelectField={onSelectField}
    />
  );

  const dayGroups = groupEventsByDay(events);
  if (dayGroups.length === 0) {
    return (
      <div className="flex h-full flex-col overflow-y-auto py-3">
        {unsavedGroup}
        <HistoryEmpty recordedSince={recordedSince} />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col gap-1.5 overflow-y-auto py-3">
      {unsavedGroup}
      {dayGroups.map((group) => (
        <Fragment key={group.key}>
          <DayDivider label={group.label} />
          {group.events.map((event) => (
            <ItemHistoryEntry
              key={event.id}
              event={event}
              context={context}
              onSelectField={onSelectField}
            />
          ))}
        </Fragment>
      ))}
      {events.some((event) => event.eventType === "ITEM_CREATED") ? null : (
        <p className="mt-auto border-t border-border px-2.5 pt-2.5 text-xs text-muted-foreground">
          Changes before {recordedSince} were not recorded.
        </p>
      )}
    </div>
  );
};

const DayDivider = ({ label }: { label: string }) => (
  <div className="flex items-center gap-3 px-2.5 pt-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
    {label}
    <span className="h-px flex-1 bg-border" aria-hidden />
  </div>
);

const HistoryEmpty = ({ recordedSince }: { recordedSince: string }) => (
  <div className="flex flex-1 flex-col items-center justify-center gap-1.5 p-6 text-center">
    <History className="mb-1.5 h-5 w-5 text-muted-foreground" aria-hidden />
    <p className="text-sm font-medium">No recorded changes</p>
    <p className="max-w-64 text-xs text-muted-foreground">
      History for this item starts {recordedSince}. Changes before that date
      were not recorded.
    </p>
  </div>
);

const HistorySkeleton = () => (
  <div className="flex flex-col gap-4 py-3">
    {[0, 1, 2].map((group) => (
      <div key={group} className="flex flex-col gap-2 px-2.5">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-3.5 w-full" />
        <Skeleton className="h-3.5 w-2/3" />
      </div>
    ))}
  </div>
);
